import React, { useDeferredValue, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FaCheckCircle, FaExclamationCircle, FaStar } from "react-icons/fa";
import {
  FiActivity,
  FiCalendar,
  FiClock,
  FiEdit3,
  FiEye,
  FiFilter,
  FiHash,
  FiLayers,
  FiMapPin,
  FiRefreshCw,
  FiSearch,
  FiTag,
  FiTrendingUp,
  FiUsers,
} from "react-icons/fi";
import {
  saveEventDiscoveryMetadata,
  subscribeEventDiscoveryRows,
  updateEventFeaturedVisibility,
  updateEventVisibilityStatus,
} from "../services/eventDiscoveryAdminService";

const COLORS = {
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  card: "#ffffff",
  primary: "#f20c53",
  secondary: "#fb6c33",
  page: "#f5f6f8",
  ok: "#16a34a",
  warn: "#b45309",
  danger: "#b91c1c",
  info: "#2563eb",
};

const STATUS_OPTIONS = [
  { value: "all", label: "All visibility states" },
  { value: "review", label: "Under review" },
  { value: "live", label: "Live / Approved" },
  { value: "draft", label: "Draft" },
  { value: "unlisted", label: "Unlisted" },
  { value: "closed", label: "Closed" },
  { value: "rejected", label: "Rejected" },
];

const FEATURED_OPTIONS = [
  { value: "all", label: "All events" },
  { value: "featured", label: "Featured" },
  { value: "standard", label: "Standard" },
];

const QUALITY_OPTIONS = [
  { value: "all", label: "All quality levels" },
  { value: "high", label: "80% and above" },
  { value: "mid", label: "50% to 79%" },
  { value: "low", label: "Below 50%" },
];

const TYPE_OPTIONS = [
  { value: "", label: "No placement" },
  { value: "hot event", label: "Hot Event" },
  { value: "recomanded event", label: "Recommended Event" },
  { value: "feture event", label: "Featured Event" },
  { value: "festivals events", label: "Festival Event" },
  { value: "Club | Parties", label: "Club | Parties" },
  { value: "live events", label: "Live Event" },
];

const normalizeArtistKey = (artist = {}) =>
  String(artist.name || artist.id || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const dedupeArtistAssociations = (artists = []) => {
  const seen = new Set();
  const output = [];
  (Array.isArray(artists) ? artists : []).forEach((artist) => {
    const key = normalizeArtistKey(artist);
    if (!key || seen.has(key)) return;
    seen.add(key);
    output.push(artist);
  });
  return output;
};

export default function EventDiscovery() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedEventId = searchParams.get("eventId") || "";

  const employee = useMemo(() => safeGetEmployee(), []);
  const role = String(employee?.role || "").toLowerCase();
  const permissions = Array.isArray(employee?.permissions) ? employee.permissions : [];
  const isSuperAdmin = role === "super_admin";

  const canView =
    isSuperAdmin ||
    permissions.some((permission) =>
      [
        "view_event_discovery",
        "manage_event_discovery",
        "view_events_venues_approval",
        "view_approved_events",
        "approve_events",
        "edit_event_status",
      ].includes(permission)
    );
  const canManage =
    isSuperAdmin ||
    permissions.some((permission) =>
      ["manage_event_discovery", "approve_events", "edit_event_status"].includes(permission)
    );
  const canFeature =
    isSuperAdmin ||
    permissions.some((permission) =>
      ["manage_event_discovery", "feature_events", "approve_events"].includes(permission)
    );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [filters, setFilters] = useState(() => getInitialFilters());
  const [metadataForm, setMetadataForm] = useState(emptyMetadataForm());
  const [visibilityDraft, setVisibilityDraft] = useState("review");
  const [actionBusy, setActionBusy] = useState("");
  const [toast, setToast] = useState(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const unsubscribe = subscribeEventDiscoveryRows(
      (nextRows) => {
        setRows(nextRows);
        setLoading(false);
        setError("");
      },
      (err) => {
        console.error("Event discovery admin stream failed:", err);
        setLoading(false);
        setRows([]);
        setError(resolveFirestoreMessage(err, "Unable to load event discovery data right now."));
      }
    );

    return () => unsubscribe();
  }, [canView, refreshNonce]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const categoryOptions = useMemo(
    () => ["all", ...uniqueValues(rows.map((row) => row.category))],
    [rows]
  );
  const cityOptions = useMemo(
    () => ["all", ...uniqueValues(rows.map((row) => row.city))],
    [rows]
  );

  const filteredRows = useMemo(() => {
    let list = [...rows];
    const query = deferredSearch.trim().toLowerCase();

    if (query) list = list.filter((row) => row.searchText.includes(query));
    if (filters.status !== "all") list = list.filter((row) => row.visibilityStatus === filters.status);
    if (filters.category !== "all") list = list.filter((row) => row.category === filters.category);
    if (filters.city !== "all") list = list.filter((row) => row.city === filters.city);
    if (filters.featured !== "all") {
      list = list.filter((row) => (filters.featured === "featured" ? row.isFeatured : !row.isFeatured));
    }
    if (filters.quality !== "all") {
      list = list.filter((row) => matchesQualityFilter(row.discoveryQualityScore, filters.quality));
    }

    return list.sort(sortForRecentEvents);
  }, [deferredSearch, filters, rows]);

  useEffect(() => {
    if (selectedEventId && rows.length && !rows.some((row) => row.id === selectedEventId)) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("eventId");
      setSearchParams(nextParams, { replace: true });
    }
  }, [rows, searchParams, selectedEventId, setSearchParams]);

  const selectedEvent = useMemo(() => {
    if (!selectedEventId) return null;
    return rows.find((row) => row.id === selectedEventId) || null;
  }, [rows, selectedEventId]);
  const selectedArtists = useMemo(
    () => dedupeArtistAssociations(selectedEvent?.artistAssociations || []),
    [selectedEvent]
  );

  useEffect(() => {
    if (!selectedEvent) {
      setMetadataForm(emptyMetadataForm());
      setVisibilityDraft("review");
      return;
    }

    setMetadataForm(buildMetadataForm(selectedEvent));
    setVisibilityDraft(selectedEvent.visibilityStatus || "review");
  }, [selectedEvent]);

  const stats = useMemo(() => {
    const total = rows.length;
    const live = rows.filter((row) => row.visibilityStatus === "live").length;
    const featured = rows.filter((row) => row.isFeatured).length;
    const weak = rows.filter((row) => row.discoveryQualityScore < 60).length;
    const avgTrend = total
      ? Math.round(rows.reduce((sum, row) => sum + Number(row.trendingScore || 0), 0) / total)
      : 0;

    return { total, live, featured, weak, avgTrend };
  }, [rows]);

  const adminContext = useMemo(
    () => ({
      docId: String(employee?.docId || ""),
      name:
        employee?.fullName ||
        employee?.full_name ||
        employee?.name ||
        employee?.mobile ||
        "Admin",
      role: String(employee?.role || ""),
    }),
    [employee]
  );

  function setFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function resetFilters() {
    setSearch("");
    setFilters(getInitialFilters());
  }

  function openEvent(eventId) {
    if (!eventId) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("eventId", eventId);
    setSearchParams(nextParams, { replace: true });
  }

  function closeEventDetail() {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("eventId");
    setSearchParams(nextParams, { replace: true });
  }

  function updateMetadataField(key, value) {
    setMetadataForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSaveMetadata() {
    if (!selectedEvent) return;
    if (!canManage) {
      showToast("You do not have permission to update event discovery metadata.", "error");
      return;
    }

    try {
      setActionBusy(`metadata:${selectedEvent.id}`);
      await saveEventDiscoveryMetadata(selectedEvent.id, metadataForm, selectedEvent, adminContext);
      showToast(`Discovery metadata saved for "${selectedEvent.title}".`, "success");
    } catch (err) {
      console.error("Event discovery metadata save failed:", err);
      showToast(resolveFirestoreMessage(err, "Unable to save event discovery metadata."), "error");
    } finally {
      setActionBusy("");
    }
  }

  async function handleToggleFeatured() {
    if (!selectedEvent) return;
    if (!canFeature) {
      showToast("You do not have permission to change featured event visibility.", "error");
      return;
    }

    try {
      setActionBusy(`feature:${selectedEvent.id}`);
      await updateEventFeaturedVisibility(selectedEvent.id, !selectedEvent.isFeatured, adminContext);
      showToast(
        `"${selectedEvent.title}" is ${selectedEvent.isFeatured ? "no longer" : "now"} featured.`,
        "success"
      );
    } catch (err) {
      console.error("Event featured update failed:", err);
      showToast(resolveFirestoreMessage(err, "Unable to update featured visibility."), "error");
    } finally {
      setActionBusy("");
    }
  }

  async function handleVisibilityUpdate() {
    if (!selectedEvent) return;
    if (!canManage) {
      showToast("You do not have permission to update event visibility.", "error");
      return;
    }

    try {
      setActionBusy(`visibility:${selectedEvent.id}`);
      await updateEventVisibilityStatus(selectedEvent.id, visibilityDraft, adminContext);
      showToast(`Visibility updated to "${formatLabel(visibilityDraft)}".`, "success");
    } catch (err) {
      console.error("Event visibility update failed:", err);
      showToast(resolveFirestoreMessage(err, "Unable to update event visibility."), "error");
    } finally {
      setActionBusy("");
    }
  }

  async function handleRowVisibilityUpdate(eventRow, nextStatus) {
    if (!eventRow) return;
    if (!canManage) {
      showToast("You do not have permission to update event visibility.", "error");
      return;
    }
    try {
      setActionBusy(`row-visibility:${eventRow.id}`);
      await updateEventVisibilityStatus(eventRow.id, nextStatus, adminContext);
      showToast(`"${eventRow.title}" moved to "${formatLabel(nextStatus)}".`, "success");
    } catch (err) {
      console.error("Event row visibility update failed:", err);
      showToast(resolveFirestoreMessage(err, "Unable to update event visibility."), "error");
    } finally {
      setActionBusy("");
    }
  }

  function showToast(message, type = "success") {
    setToast({ message, type });
  }

  if (!canView) {
    return (
      <div style={deniedWrap}>
        <div style={deniedCard}>
          <FaExclamationCircle size={34} color="#e53935" />
          <h3 style={{ margin: "1rem 0 0.35rem", color: COLORS.text }}>Access Denied</h3>
          <p style={{ margin: 0, color: COLORS.subtext }}>
            You do not have permission to access event discovery management.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={pageWrap}>
      <style>{responsiveStyles}</style>
      {!selectedEventId ? (
      <div style={heroCard}>
        <div style={heroCopy}>
          <div style={heroIcon}>
            <FiTrendingUp />
          </div>
          <div>
            <h2 style={heroTitle}>All Events</h2>
            <p style={heroSub}>
              Review every event in one list, sorted by the newest created event first. Open detail view to
              approve, feature, edit discovery metadata, and manage visibility.
            </p>
          </div>
        </div>

        <div style={statsGrid}>
          <StatCard label="Events" value={stats.total} tone="neutral" />
          <StatCard label="Live" value={stats.live} tone="ok" />
          <StatCard label="Featured" value={stats.featured} tone="brand" />
          <StatCard label="Weak Metadata" value={stats.weak} tone="warn" />
          <StatCard label="Avg. Trend" value={stats.avgTrend} tone="info" />
        </div>
      </div>
      ) : null}

      {!selectedEventId ? (
      <div style={controlsCard}>
        <div style={controlsRow}>
          <div style={{ ...searchBox, flex: 1 }}>
            <FiSearch style={{ color: COLORS.subtext }} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search title, organiser, city, category, tags, artists, event id..."
              style={searchInput}
            />
          </div>

          <button
            type="button"
            style={ghostButton}
            onClick={() => setRefreshNonce((value) => value + 1)}
          >
            <FiRefreshCw />
            <span>Refresh</span>
          </button>

          <button type="button" style={ghostButton} onClick={resetFilters}>
            <FiFilter />
            <span>Reset filters</span>
          </button>
        </div>

        <div style={filterGrid}>
          <FilterSelect
            label="Visibility"
            value={filters.status}
            onChange={(value) => setFilter("status", value)}
            options={STATUS_OPTIONS}
          />
          <FilterSelect
            label="Category"
            value={filters.category}
            onChange={(value) => setFilter("category", value)}
            options={categoryOptions}
          />
          <FilterSelect
            label="City"
            value={filters.city}
            onChange={(value) => setFilter("city", value)}
            options={cityOptions}
          />
          <FilterSelect
            label="Featured"
            value={filters.featured}
            onChange={(value) => setFilter("featured", value)}
            options={FEATURED_OPTIONS}
          />
          <FilterSelect
            label="Metadata Quality"
            value={filters.quality}
            onChange={(value) => setFilter("quality", value)}
            options={QUALITY_OPTIONS}
          />
        </div>
      </div>
      ) : null}

      {error ? (
        <div style={errorCard}>
          <FaExclamationCircle />
          <span>{error}</span>
        </div>
      ) : null}

      <div className="event-discovery-grid" style={layoutGrid}>
        {!selectedEventId ? (
        <div className="event-discovery-queue" style={queueCard}>
          <div style={sectionHead}>
            <div>
              <div style={sectionTitle}>All Events</div>
              <div style={sectionCopy}>
                {filteredRows.length} event{filteredRows.length === 1 ? "" : "s"} match the current
                filters. Recent created events are shown first.
              </div>
            </div>
          </div>

          {loading ? (
            <EmptyState
              title="Loading events"
              copy="Syncing event discovery data from Firestore."
            />
          ) : filteredRows.length === 0 ? (
            <EmptyState
              title="No events found"
              copy="Try broader filters or a different search term to review more event metadata."
            />
          ) : (
            <div style={allEventsList}>
              {filteredRows.map((eventRow) => {
                const selected = selectedEvent?.id === eventRow.id;
                return (
                  <div
                    key={eventRow.id}
                    className="event-discovery-card"
                    style={selected ? allEventRowActive : allEventRow}
                  >
                    <div style={allEventMain}>
                      <div style={eventThumbWrap}>
                        {eventRow.images?.[0] ? (
                          <img src={eventRow.images[0]} alt={eventRow.title} style={eventThumb} />
                        ) : (
                          <FaStar />
                        )}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={queueTitle}>{eventRow.title}</div>
                        <div style={queueMeta}>
                          {eventRow.organiserName} • {eventRow.cityLabel}
                        </div>
                        <div style={allEventMiniMeta}>
                          <span>{eventRow.startLabel}</span>
                          <span>{eventRow.categoryLabel}</span>
                          <span>{eventRow.priceRangeLabel}</span>
                          <span>Created {formatDateTime(eventRow.createdAt)}</span>
                        </div>
                      </div>
                    </div>

                    <div style={queueBadgeRow}>
                      <Badge tone={badgeToneForStatus(eventRow.visibilityStatus)}>
                        {eventRow.visibilityLabel}
                      </Badge>
                      <Badge tone={eventRow.isFeatured ? "brand" : "neutral"}>
                        {eventRow.isFeatured ? "Featured" : "Standard"}
                      </Badge>
                      <Badge tone={qualityTone(eventRow.discoveryQualityScore)}>
                        {eventRow.discoveryQualityScore}% quality
                      </Badge>
                    </div>

                    <div style={allEventActions}>
                      <select
                        value={eventRow.visibilityStatus}
                        onChange={(event) => handleRowVisibilityUpdate(eventRow, event.target.value)}
                        style={listStatusSelect}
                        disabled={!canManage || actionBusy === `row-visibility:${eventRow.id}`}
                      >
                        {STATUS_OPTIONS.filter((option) => option.value !== "all").map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <button type="button" style={primaryButton} onClick={() => openEvent(eventRow.id)}>
                        <FiEye />
                        <span>Detail View</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        ) : null}

        {selectedEventId ? (
        <div style={detailCard}>
          <div style={sectionHead}>
            <div>
              <div style={sectionTitle}>Event Discovery Detail</div>
              <div style={sectionCopy}>
                Review metadata quality, improve discovery tags, manage featured placement, and control
                event visibility without changing the existing organiser workflow.
              </div>
            </div>
            <button type="button" style={ghostButton} onClick={closeEventDetail}>
              All Events
            </button>
            {selectedEvent ? (
              <Badge tone={qualityTone(selectedEvent.discoveryQualityScore)}>
                {selectedEvent.discoveryQualityScore}% quality
              </Badge>
            ) : null}
          </div>
          {loading ? (
            <EmptyState
              title="Loading event detail"
              copy="Select an event to inspect discovery metadata and recommendation support fields."
            />
          ) : !selectedEvent ? (
            <EmptyState
              title="No event selected"
              copy="Choose an event from the queue to open the event discovery workspace."
            />
          ) : (
            <div style={detailBody}>
              <div style={detailHero}>
                <div>
                  <h3 style={detailTitle}>{selectedEvent.title}</h3>
                  <p style={detailSub}>
                    {selectedEvent.cityLabel} • {selectedEvent.venueLabel} • {selectedEvent.organiserName}
                  </p>
                </div>

                <div style={detailBadgeWrap}>
                  <Badge tone={badgeToneForStatus(selectedEvent.visibilityStatus)}>
                    {selectedEvent.visibilityLabel}
                  </Badge>
                  <Badge tone={selectedEvent.isFeatured ? "brand" : "neutral"}>
                    {selectedEvent.isFeatured ? "Featured" : "Standard"}
                  </Badge>
                  <Badge tone="info">Trend {selectedEvent.trendingScore}</Badge>
                </div>
              </div>

              <div style={actionPanel}>
                <div style={sectionTitle}>Admin Actions</div>
                <div style={actionGrid}>
                  <ActionButton
                    label={selectedEvent.isFeatured ? "Unfeature Event" : "Feature Event"}
                    tone="brand"
                    disabled={!canFeature || actionBusy === `feature:${selectedEvent.id}`}
                    onClick={handleToggleFeatured}
                  />

                  <div style={visibilityWrap}>
                    <select
                      value={visibilityDraft}
                      onChange={(event) => setVisibilityDraft(event.target.value)}
                      style={inputStyle}
                      disabled={!canManage}
                    >
                      {STATUS_OPTIONS.filter((option) => option.value !== "all").map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      style={ghostButton}
                      disabled={!canManage || actionBusy === `visibility:${selectedEvent.id}`}
                      onClick={handleVisibilityUpdate}
                    >
                      <FiEye />
                      <span>
                        {actionBusy === `visibility:${selectedEvent.id}`
                          ? "Updating..."
                          : "Update visibility"}
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              <div style={metricsGrid}>
                <MetricCard label="Start Time" value={selectedEvent.startLabel} icon={<FiCalendar />} />
                <MetricCard label="Price Range" value={selectedEvent.priceRangeLabel} icon={<FiTag />} />
                <MetricCard label="Bookings" value={String(selectedEvent.bookingCount || 0)} icon={<FiUsers />} />
                <MetricCard label="Views" value={String(selectedEvent.viewCount || 0)} icon={<FiActivity />} />
                <MetricCard label="Artists" value={String(selectedArtists.length || 0)} icon={<FiUsers />} />
                <MetricCard
                  label="Updated"
                  value={formatDateTime(selectedEvent.updatedAt || selectedEvent.createdAt)}
                  icon={<FiClock />}
                />
              </div>

              <div style={qualityCard}>
                <div style={sectionHead}>
                  <div>
                    <div style={sectionTitle}>Discovery Metadata Quality</div>
                    <div style={sectionCopy}>
                      This score tracks whether key recommendation and discovery fields are strong enough for
                      filtering, ranking, and featured placement.
                    </div>
                  </div>
                  <Badge tone={qualityTone(selectedEvent.discoveryQualityScore)}>
                    {selectedEvent.discoveryQualityScore}%
                  </Badge>
                </div>

                <div style={progressTrack}>
                  <div
                    style={{
                      ...progressFill,
                      width: `${selectedEvent.discoveryQualityScore}%`,
                      background: progressColor(selectedEvent.discoveryQualityScore),
                    }}
                  />
                </div>

                {selectedEvent.missingMetadataFields.length ? (
                  <div style={missingWrap}>
                    {selectedEvent.missingMetadataFields.map((field) => (
                      <Badge key={field} tone="warn">
                        Missing: {formatLabel(field)}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: COLORS.ok, fontWeight: 800 }}>
                    Discovery metadata is complete for the main recommendation signals.
                  </div>
                )}
              </div>

              <div style={editorCard}>
                <div style={sectionHead}>
                  <div>
                    <div style={sectionTitle}>Discovery Metadata Editor</div>
                    <div style={sectionCopy}>
                      Improve category, genre, city, tags, trending support fields, and venue metadata without
                      creating a separate event system.
                    </div>
                  </div>
                  <button
                    type="button"
                    style={primaryButton}
                    disabled={!canManage || actionBusy === `metadata:${selectedEvent.id}`}
                    onClick={handleSaveMetadata}
                  >
                    <FiEdit3 />
                    <span>
                      {actionBusy === `metadata:${selectedEvent.id}` ? "Saving..." : "Save discovery fields"}
                    </span>
                  </button>
                </div>

                <div style={formGrid}>
                  <Field label="Category">
                    <input
                      value={metadataForm.category}
                      onChange={(event) => updateMetadataField("category", event.target.value)}
                      style={inputStyle}
                      disabled={!canManage}
                    />
                  </Field>
                  <Field label="Genre">
                    <input
                      value={metadataForm.eventGenre}
                      onChange={(event) => updateMetadataField("eventGenre", event.target.value)}
                      style={inputStyle}
                      disabled={!canManage}
                    />
                  </Field>
                  <Field label="City">
                    <input
                      value={metadataForm.city}
                      onChange={(event) => updateMetadataField("city", event.target.value)}
                      style={inputStyle}
                      disabled={!canManage}
                    />
                  </Field>
                  <Field label="Venue Name">
                    <input
                      value={metadataForm.venueName}
                      onChange={(event) => updateMetadataField("venueName", event.target.value)}
                      style={inputStyle}
                      disabled={!canManage}
                    />
                  </Field>
                  <Field label="Discovery Placement">
                    <select
                      value={metadataForm.typeOfEvent}
                      onChange={(event) => updateMetadataField("typeOfEvent", event.target.value)}
                      style={inputStyle}
                      disabled={!canManage}
                    >
                      {TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Price Min">
                    <input
                      type="number"
                      value={metadataForm.priceMin}
                      onChange={(event) => updateMetadataField("priceMin", event.target.value)}
                      style={inputStyle}
                      disabled={!canManage}
                    />
                  </Field>
                  <Field label="Price Max">
                    <input
                      type="number"
                      value={metadataForm.priceMax}
                      onChange={(event) => updateMetadataField("priceMax", event.target.value)}
                      style={inputStyle}
                      disabled={!canManage}
                    />
                  </Field>
                  <Field label="Booking Count">
                    <input
                      type="number"
                      value={metadataForm.bookingCount}
                      onChange={(event) => updateMetadataField("bookingCount", event.target.value)}
                      style={inputStyle}
                      disabled={!canManage}
                    />
                  </Field>
                  <Field label="View Count">
                    <input
                      type="number"
                      value={metadataForm.viewCount}
                      onChange={(event) => updateMetadataField("viewCount", event.target.value)}
                      style={inputStyle}
                      disabled={!canManage}
                    />
                  </Field>
                  <Field label="Trending Score">
                    <input
                      type="number"
                      value={metadataForm.trendingScore}
                      onChange={(event) => updateMetadataField("trendingScore", event.target.value)}
                      style={inputStyle}
                      disabled={!canManage}
                    />
                  </Field>
                  <Field label="Discovery Tags" full>
                    <textarea
                      value={metadataForm.discoveryTags}
                      onChange={(event) => updateMetadataField("discoveryTags", event.target.value)}
                      style={textareaStyle}
                      disabled={!canManage}
                      placeholder="Comma-separated tags like rooftop, EDM, weekend, premium..."
                    />
                  </Field>
                </div>
              </div>

              <div style={infoGrid}>
                <div style={infoCard}>
                  <div style={sectionTitle}>Associated Artists</div>
                  <div style={chipWrap}>
                    {selectedArtists.length ? (
                      selectedArtists.map((artist) => (
                        <Badge key={artist.id} tone="neutral">
                          {artist.name}
                          {artist.categoryLabel ? ` • ${artist.categoryLabel}` : ""}
                        </Badge>
                      ))
                    ) : (
                      <span style={mutedText}>No artist associations saved for this event.</span>
                    )}
                  </div>
                </div>

                <div style={infoCard}>
                  <div style={sectionTitle}>Media Coverage</div>
                  {selectedEvent.images.length ? (
                    <div style={mediaGrid}>
                      {selectedEvent.images.map((image) => (
                        <button
                          key={image}
                          type="button"
                          style={imageButton}
                          onClick={() => window.open(image, "_blank", "noopener,noreferrer")}
                        >
                          <img src={image} alt={selectedEvent.title} style={imageThumb} />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <span style={mutedText}>No banner or poster metadata available.</span>
                  )}
                </div>

                <div style={infoCard}>
                  <div style={sectionTitle}>Event Discovery Context</div>
                  <div style={detailList}>
                    <DetailRow label="Event ID" value={selectedEvent.id} icon={<FiHash />} />
                    <DetailRow label="Organiser" value={selectedEvent.organiserName} icon={<FiUsers />} />
                    <DetailRow label="Organiser ID" value={selectedEvent.organiserId || "Not shared"} icon={<FiHash />} />
                    <DetailRow label="Venue" value={selectedEvent.venueLabel} icon={<FiMapPin />} />
                    <DetailRow label="Category" value={selectedEvent.categoryLabel} icon={<FiLayers />} />
                    <DetailRow label="Genre" value={selectedEvent.genreLabel} icon={<FiTag />} />
                    <DetailRow label="Type" value={labelForType(selectedEvent.typeOfEvent)} icon={<FaStar />} />
                    <DetailRow label="Start" value={selectedEvent.startLabel} icon={<FiCalendar />} />
                  </div>
                </div>
              </div>

              <div style={infoCard}>
                <div style={sectionTitle}>Description and Tags</div>
                <p style={descriptionCopy}>
                  {selectedEvent.description || "No event description has been added yet."}
                </p>
                <div style={chipWrap}>
                  {selectedEvent.discoveryTags.length ? (
                    selectedEvent.discoveryTags.map((tag) => (
                      <Badge key={tag} tone="info">
                        Tag: {tag}
                      </Badge>
                    ))
                  ) : (
                    <span style={mutedText}>No discovery tags added yet.</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        ) : null}
      </div>

      {toast ? (
        <div style={toast.type === "error" ? toastError : toastSuccess}>
          {toast.type === "error" ? <FaExclamationCircle /> : <FaCheckCircle />}
          <span>{toast.message}</span>
        </div>
      ) : null}
    </div>
  );
}

function Field({ label, children, full = false }) {
  return (
    <label style={{ ...fieldWrap, gridColumn: full ? "1 / -1" : "auto" }}>
      <span style={fieldLabel}>{label}</span>
      {children}
    </label>
  );
}

function FilterSelect({ label, value, onChange, options }) {
  const normalizedOptions = options.map((option) =>
    typeof option === "string"
      ? { value: option, label: option === "all" ? "All" : option }
      : option
  );

  return (
    <label style={filterLabel}>
      <span style={filterCaption}>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)} style={filterSelectStyle}>
        {normalizedOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function StatCard({ label, value, tone = "neutral" }) {
  const palette = {
    neutral: { background: "#ffffff", border: "#e2e8f0", color: "#0f172a" },
    warn: { background: "#fff7ed", border: "#fdba74", color: "#b45309" },
    ok: { background: "#ecfdf5", border: "#bbf7d0", color: "#166534" },
    brand: { background: "#fff1f7", border: "#f9a8d4", color: "#be185d" },
    danger: { background: "#fef2f2", border: "#fecaca", color: "#b91c1c" },
    info: { background: "#eff6ff", border: "#bfdbfe", color: "#1d4ed8" },
  }[tone];

  return (
    <div style={{ background: palette.background, border: `1px solid ${palette.border}`, borderRadius: 14, padding: "0.8rem 0.95rem" }}>
      <div style={{ color: COLORS.subtext, fontSize: 12, fontWeight: 700 }}>{label}</div>
      <div style={{ marginTop: 6, color: palette.color, fontSize: 22, fontWeight: 900 }}>{value}</div>
    </div>
  );
}

function MetricCard({ label, value, icon }) {
  return (
    <div style={metricCard}>
      <div style={metricLabel}>
        {icon}
        <span>{label}</span>
      </div>
      <div style={metricValue}>{value || "Not shared"}</div>
    </div>
  );
}

function Badge({ children, tone = "neutral" }) {
  const palette = {
    neutral: { background: "#f8fafc", color: "#334155", border: "#e2e8f0" },
    ok: { background: "#ecfdf5", color: "#166534", border: "#bbf7d0" },
    warn: { background: "#fff7ed", color: "#b45309", border: "#fdba74" },
    danger: { background: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
    brand: { background: "#fff1f7", color: "#be185d", border: "#f9a8d4" },
    info: { background: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
  }[tone];

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 999, border: `1px solid ${palette.border}`, background: palette.background, color: palette.color, fontSize: 12, fontWeight: 800 }}>
      {children}
    </span>
  );
}

function ActionButton({ label, onClick, disabled, tone = "neutral" }) {
  const palette = {
    ok: { background: "#16a34a", color: "#ffffff" },
    danger: { background: "#dc2626", color: "#ffffff" },
    warn: { background: "#ea580c", color: "#ffffff" },
    brand: { background: "linear-gradient(90deg,#f20c53,#fb6c33)", color: "#ffffff" },
    neutral: { background: "#ffffff", color: "#0f172a" },
  }[tone];

  return (
    <button
      type="button"
      style={{ minHeight: 42, borderRadius: 12, border: tone === "neutral" ? `1px solid ${COLORS.border}` : "none", background: disabled ? "#e2e8f0" : palette.background, color: disabled ? "#64748b" : palette.color, padding: "0.75rem 0.9rem", fontWeight: 800, cursor: disabled ? "not-allowed" : "pointer" }}
      disabled={disabled}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function DetailRow({ label, value, icon }) {
  return (
    <div style={detailRow}>
      <div style={detailKey}>
        {icon}
        <span>{label}</span>
      </div>
      <div style={detailValue}>{value || "Not shared"}</div>
    </div>
  );
}

function EmptyState({ title, copy }) {
  return (
    <div style={emptyState}>
      <div style={emptyIcon}>
        <FaStar />
      </div>
      <div style={{ fontWeight: 900, color: COLORS.text }}>{title}</div>
      <div style={{ color: COLORS.subtext, maxWidth: 460 }}>{copy}</div>
    </div>
  );
}

function getInitialFilters() {
  return { status: "all", category: "all", city: "all", featured: "all", quality: "all" };
}

function emptyMetadataForm() {
  return {
    category: "",
    eventGenre: "",
    city: "",
    venueName: "",
    typeOfEvent: "",
    discoveryTags: "",
    priceMin: "",
    priceMax: "",
    bookingCount: "",
    viewCount: "",
    trendingScore: "",
  };
}

function buildMetadataForm(eventRow) {
  return {
    category: eventRow.category || "",
    eventGenre: eventRow.eventGenre || "",
    city: eventRow.city || "",
    venueName: eventRow.venueName || "",
    typeOfEvent: eventRow.typeOfEvent || "",
    discoveryTags: eventRow.discoveryTags.join(", "),
    priceMin: eventRow.priceMin ?? "",
    priceMax: eventRow.priceMax ?? "",
    bookingCount: eventRow.bookingCount ?? "",
    viewCount: eventRow.viewCount ?? "",
    trendingScore: eventRow.trendingScore ?? "",
  };
}

function safeGetEmployee() {
  try {
    const raw = localStorage.getItem("employee");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function uniqueValues(values = []) {
  const lookup = new Map();
  values.forEach((entry) => {
    const text = String(entry ?? "").trim();
    if (!text) return;
    const key = text.toLowerCase();
    if (!lookup.has(key)) lookup.set(key, text);
  });
  return Array.from(lookup.values()).sort((left, right) => left.localeCompare(right));
}

function matchesQualityFilter(score, filter) {
  const value = Number(score || 0);
  if (filter === "high") return value >= 80;
  if (filter === "mid") return value >= 50 && value < 80;
  if (filter === "low") return value < 50;
  return true;
}

function sortForRecentEvents(left, right) {
  const rightStamp = Number(right?.createdAtMs || right?.updatedAtMs || right?.startMs || 0);
  const leftStamp = Number(left?.createdAtMs || left?.updatedAtMs || left?.startMs || 0);
  return rightStamp - leftStamp;
}

function badgeToneForStatus(status) {
  if (status === "live") return "ok";
  if (status === "approved") return "info";
  if (status === "review") return "warn";
  if (status === "rejected") return "danger";
  return "neutral";
}

function qualityTone(score) {
  if (score >= 80) return "ok";
  if (score >= 50) return "info";
  return "warn";
}

function progressColor(score) {
  if (score >= 80) return "linear-gradient(90deg,#16a34a,#22c55e)";
  if (score >= 50) return "linear-gradient(90deg,#2563eb,#38bdf8)";
  return "linear-gradient(90deg,#ea580c,#f97316)";
}

function labelForType(value) {
  const match = TYPE_OPTIONS.find((option) => option.value === value);
  return match?.label || "No placement";
}

function formatLabel(value) {
  return String(value || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase())
    .trim();
}

function formatDateTime(value) {
  if (!value) return "Not available";
  const date =
    typeof value?.toDate === "function"
      ? value.toDate()
      : typeof value?.seconds === "number"
        ? new Date(value.seconds * 1000)
        : new Date(value);
  if (!Number.isFinite(date.getTime())) return "Not available";
  try {
    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
}

function resolveFirestoreMessage(error, fallback) {
  const code = String(error?.code || "").toLowerCase();
  if (code.includes("permission-denied")) return "You do not have permission to complete this admin action.";
  if (code.includes("unavailable")) return "Firestore is temporarily unavailable. Please try again.";
  if (code.includes("not-found")) return "The selected event could not be found.";
  if (code.includes("failed-precondition")) return "Firestore needs an additional index before this request can complete.";
  return fallback;
}

const responsiveStyles = `
  @media (max-width: 980px) {
    .event-discovery-card {
      min-width: 280px !important;
      grid-template-columns: 1fr !important;
    }
  }
`;

const pageWrap = { minHeight: "100vh", background: COLORS.page, padding: 16, display: "grid", gap: 16 };
const heroCard = { background: "linear-gradient(135deg, rgba(242,12,83,0.98), rgba(251,108,51,0.94))", borderRadius: 24, padding: "1.25rem", color: "#ffffff", display: "grid", gap: 18, boxShadow: "0 22px 44px rgba(242,12,83,0.2)" };
const heroCopy = { display: "flex", alignItems: "flex-start", gap: 14, flexWrap: "wrap" };
const heroIcon = { width: 56, height: 56, borderRadius: 18, display: "grid", placeItems: "center", background: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.22)", fontSize: 24 };
const heroTitle = { margin: 0, fontSize: 28, fontWeight: 900, letterSpacing: "-0.02em" };
const heroSub = { margin: "0.5rem 0 0", maxWidth: 860, color: "rgba(255,255,255,0.88)", fontSize: 14, lineHeight: 1.6 };
const statsGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10 };
const controlsCard = { background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 20, padding: "1rem", display: "grid", gap: 14, boxShadow: "0 10px 30px rgba(15,23,42,0.05)" };
const controlsRow = { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" };
const searchBox = { minHeight: 46, display: "flex", alignItems: "center", gap: 10, background: "#ffffff", border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "0 0.9rem" };
const searchInput = { flex: 1, minWidth: 220, border: "none", outline: "none", background: "transparent", color: COLORS.text, fontSize: 14 };
const filterGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 };
const filterLabel = { display: "grid", gap: 6 };
const filterCaption = { color: COLORS.subtext, fontSize: 12, fontWeight: 800 };
const filterSelectStyle = { minHeight: 44, borderRadius: 12, border: `1px solid ${COLORS.border}`, background: "#ffffff", color: COLORS.text, padding: "0 0.85rem", outline: "none", fontWeight: 700 };
const layoutGrid = { display: "grid", gridTemplateColumns: "1fr", gap: 16, alignItems: "start" };
const queueCard = { background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 20, padding: "1rem", display: "grid", gap: 14, boxShadow: "0 10px 30px rgba(15,23,42,0.05)", minWidth: 0 };
const detailCard = { background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 20, padding: "1rem", display: "grid", gap: 14, minWidth: 0, boxShadow: "0 10px 30px rgba(15,23,42,0.05)" };
const sectionHead = { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" };
const sectionTitle = { color: COLORS.text, fontSize: 18, fontWeight: 900 };
const sectionCopy = { marginTop: 4, color: COLORS.subtext, fontSize: 13, lineHeight: 1.6, maxWidth: 760 };
const queueTitle = { fontSize: 16, fontWeight: 900, color: COLORS.text };
const queueMeta = { marginTop: 4, color: COLORS.subtext, fontSize: 13, lineHeight: 1.5 };
const queueBadgeRow = { display: "flex", flexWrap: "wrap", gap: 8 };
const allEventsList = { display: "grid", gap: 10 };
const allEventRow = { border: `1px solid ${COLORS.border}`, background: "#ffffff", borderRadius: 16, padding: "0.95rem", display: "grid", gridTemplateColumns: "minmax(0, 1.6fr) minmax(180px, auto) minmax(240px, auto)", alignItems: "center", gap: 12, color: COLORS.text, boxShadow: "0 8px 20px rgba(15,23,42,0.04)" };
const allEventRowActive = { ...allEventRow, border: "1px solid rgba(242,12,83,0.28)", background: "linear-gradient(180deg,#fff7fb 0%, #ffffff 100%)", boxShadow: "0 16px 30px rgba(242,12,83,0.1)" };
const allEventMain = { display: "grid", gridTemplateColumns: "64px minmax(0, 1fr)", gap: 12, alignItems: "center", minWidth: 0 };
const eventThumbWrap = { width: 64, height: 64, borderRadius: 14, border: `1px solid ${COLORS.border}`, background: "#f8fafc", color: COLORS.primary, display: "grid", placeItems: "center", overflow: "hidden", fontSize: 22 };
const eventThumb = { width: "100%", height: "100%", objectFit: "cover", display: "block" };
const allEventMiniMeta = { marginTop: 7, display: "flex", flexWrap: "wrap", gap: "6px 12px", color: COLORS.subtext, fontSize: 12, fontWeight: 800 };
const allEventActions = { display: "flex", gap: 8, alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap" };
const listStatusSelect = { minHeight: 42, borderRadius: 12, border: `1px solid ${COLORS.border}`, background: "#ffffff", color: COLORS.text, padding: "0 0.75rem", outline: "none", fontWeight: 800 };
const detailBody = { display: "grid", gap: 16 };
const detailHero = { display: "flex", justifyContent: "space-between", gap: 14, flexWrap: "wrap", padding: "1rem", borderRadius: 18, border: `1px solid ${COLORS.border}`, background: "linear-gradient(180deg,#fff7fb 0%, #ffffff 100%)" };
const detailTitle = { margin: 0, fontSize: 24, fontWeight: 900, color: COLORS.text };
const detailSub = { margin: "0.35rem 0 0", color: COLORS.subtext, lineHeight: 1.6 };
const detailBadgeWrap = { display: "flex", gap: 8, flexWrap: "wrap", alignSelf: "flex-start" };
const actionPanel = { display: "grid", gap: 12, background: "#ffffff", border: `1px solid ${COLORS.border}`, borderRadius: 18, padding: "1rem" };
const actionGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 };
const visibilityWrap = { display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 10, alignItems: "center" };
const metricsGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 };
const metricCard = { border: `1px solid ${COLORS.border}`, borderRadius: 16, padding: "0.95rem", background: "#ffffff", display: "grid", gap: 8 };
const metricLabel = { display: "inline-flex", alignItems: "center", gap: 8, color: COLORS.subtext, fontSize: 12, fontWeight: 900 };
const metricValue = { color: COLORS.text, fontSize: 16, fontWeight: 800, lineHeight: 1.4 };
const qualityCard = { border: `1px solid ${COLORS.border}`, borderRadius: 18, padding: "1rem", background: "#ffffff", display: "grid", gap: 12 };
const progressTrack = { width: "100%", height: 12, borderRadius: 999, background: "#e2e8f0", overflow: "hidden" };
const progressFill = { height: "100%", borderRadius: 999, transition: "width .25s ease" };
const missingWrap = { display: "flex", flexWrap: "wrap", gap: 8 };
const editorCard = { border: `1px solid ${COLORS.border}`, borderRadius: 18, padding: "1rem", background: "#ffffff", display: "grid", gap: 16 };
const formGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 };
const fieldWrap = { display: "grid", gap: 6 };
const fieldLabel = { color: COLORS.subtext, fontSize: 12, fontWeight: 800 };
const inputStyle = { minHeight: 44, borderRadius: 12, border: `1px solid ${COLORS.border}`, background: "#ffffff", color: COLORS.text, padding: "0 0.85rem", outline: "none", fontSize: 14 };
const textareaStyle = { minHeight: 110, borderRadius: 12, border: `1px solid ${COLORS.border}`, background: "#ffffff", color: COLORS.text, padding: "0.75rem 0.85rem", outline: "none", fontSize: 14, resize: "vertical" };
const infoGrid = { display: "grid", gridTemplateColumns: "1fr", gap: 12 };
const infoCard = { border: `1px solid ${COLORS.border}`, borderRadius: 18, padding: "1rem", background: "#ffffff", display: "grid", gap: 12 };
const chipWrap = { display: "flex", flexWrap: "wrap", gap: 8 };
const mediaGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 240px))", gap: 10 };
const imageButton = { border: `1px solid ${COLORS.border}`, background: "#ffffff", borderRadius: 14, overflow: "hidden", padding: 0, cursor: "pointer" };
const imageThumb = { width: "100%", height: 96, objectFit: "cover", display: "block" };
const detailList = { display: "grid", gap: 10 };
const detailRow = { display: "grid", gridTemplateColumns: "minmax(120px, 160px) minmax(0, 1fr)", gap: 12, alignItems: "start" };
const detailKey = { display: "inline-flex", alignItems: "center", gap: 8, color: COLORS.subtext, fontSize: 12, fontWeight: 800 };
const detailValue = { color: COLORS.text, fontWeight: 700, lineHeight: 1.5, wordBreak: "break-word" };
const descriptionCopy = { margin: 0, color: COLORS.subtext, lineHeight: 1.7 };
const mutedText = { color: COLORS.subtext, fontSize: 13 };
const primaryButton = { minHeight: 44, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, borderRadius: 12, border: "none", background: "linear-gradient(90deg,#f20c53,#fb6c33)", color: "#ffffff", padding: "0.75rem 1rem", fontWeight: 900, cursor: "pointer", boxShadow: "0 16px 28px rgba(242,12,83,0.16)" };
const ghostButton = { minHeight: 44, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, borderRadius: 12, border: `1px solid ${COLORS.border}`, background: "#ffffff", color: COLORS.text, padding: "0.75rem 1rem", fontWeight: 800, cursor: "pointer" };
const emptyState = { minHeight: 220, borderRadius: 18, border: `1px dashed ${COLORS.border}`, background: "#f8fafc", display: "grid", placeItems: "center", gap: 10, textAlign: "center", padding: "1.5rem" };
const emptyIcon = { width: 56, height: 56, borderRadius: 18, display: "grid", placeItems: "center", background: "#ffffff", border: `1px solid ${COLORS.border}`, color: COLORS.primary, fontSize: 24 };
const errorCard = { display: "inline-flex", alignItems: "center", gap: 10, color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 14, padding: "0.85rem 1rem", fontWeight: 700 };
const toastBase = { position: "fixed", right: 20, bottom: 20, zIndex: 10000, display: "inline-flex", alignItems: "center", gap: 10, borderRadius: 14, padding: "0.85rem 1rem", fontWeight: 800, boxShadow: "0 18px 40px rgba(15,23,42,0.18)" };
const toastSuccess = { ...toastBase, background: "#ecfdf5", color: "#166534", border: "1px solid #bbf7d0" };
const toastError = { ...toastBase, background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca" };
const deniedWrap = { minHeight: "100vh", background: COLORS.page, padding: "2rem", display: "grid", placeItems: "center" };
const deniedCard = { width: "100%", maxWidth: 440, borderRadius: 20, background: "#ffffff", border: `1px solid ${COLORS.border}`, boxShadow: "0 18px 36px rgba(15,23,42,0.12)", padding: "2rem", textAlign: "center" };
